import {
  BasketIcon,
  BusIcon,
  CarIcon,
  CarrotIcon,
  CheckCircleIcon,
  CouchIcon,
  CowIcon,
  CreditCardIcon,
  DotsThreeCircleIcon,
  FirstAidKitIcon,
  ForkKnifeIcon,
  GasPumpIcon,
  GiftIcon,
  HouseIcon,
  type Icon,
  LightningIcon,
  MoneyIcon,
  PawPrintIcon,
  PiggyBankIcon,
  PillIcon,
  PlusIcon,
  PopcornIcon,
  ReceiptIcon,
  RepeatIcon,
  ScissorsIcon,
  ShieldCheckIcon,
  SpinnerGapIcon,
  StudentIcon,
  TShirtIcon,
  TicketIcon,
  TrendUpIcon,
  WalletIcon,
  WifiHighIcon,
} from '@phosphor-icons/react'
import { type PlainDate, formatMoney, isPlainDate, todayIn } from '@paybae/shared'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { Card, EmptyState, SectionHeader } from '@/components/Card'
import { Donut, type DonutSegment } from '@/components/Donut'
import { ErrorState, Skeleton } from '@/components/Estado'
import { AvisoError, Campo, FormSheet, Guardar, Monto } from '@/components/Form'
import { Money } from '@/components/Money'
import { PersonDot } from '@/components/PersonAvatar'
import { ScreenHeader } from '@/components/ScreenHeader'
import { ApiError, type RecurrenceFrequency, type RecurringExpense } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { personPalette } from '@/lib/persons'
import { useRecurring, useRegistrarOcurrencia } from '@/lib/queries'
import { useSession } from '@/lib/session'

/**
 * Gastos fijos.
 *
 * Son los que ya están decididos —renta, colegiatura, la luz— y por eso no se
 * capturan uno por uno cada mes: se definen una vez y aquí solo se confirma que
 * cayeron. La pantalla responde una sola pregunta, que es la que importa a
 * mitad de quincena: **qué me falta pagar de lo que ya sé que debo pagar**.
 *
 * Lo que va a tarjeta se marca aparte en todas partes. Ese dinero todavía no
 * sale del efectivo: llega como saldo de la tarjeta y se descuenta cuando vence
 * su estado de cuenta. Sumarlo con lo demás haría creer que ya se fue.
 */

/**
 * El servidor guarda el icono de la categoría como texto (`house`, `lightning`)
 * para no atar la base a una librería concreta, así que la traducción vive en
 * la pantalla. Un nombre que esta versión no conozca cae en el neutro: mejor un
 * icono genérico que una fila rota.
 */
const ICONOS: Record<string, Icon> = {
  house: HouseIcon,
  car: CarIcon,
  lightning: LightningIcon,
  'wifi-high': WifiHighIcon,
  repeat: RepeatIcon,
  'shield-check': ShieldCheckIcon,
  student: StudentIcon,
  basket: BasketIcon,
  carrot: CarrotIcon,
  cow: CowIcon,
  'fork-knife': ForkKnifeIcon,
  'gas-pump': GasPumpIcon,
  bus: BusIcon,
  'first-aid-kit': FirstAidKitIcon,
  pill: PillIcon,
  popcorn: PopcornIcon,
  't-shirt': TShirtIcon,
  couch: CouchIcon,
  'paw-print': PawPrintIcon,
  gift: GiftIcon,
  scissors: ScissorsIcon,
  'dots-three-circle': DotsThreeCircleIcon,
  'credit-card': CreditCardIcon,
  'piggy-bank': PiggyBankIcon,
  money: MoneyIcon,
  ticket: TicketIcon,
  'trend-up': TrendUpIcon,
}

function iconoDe(nombre: string | null | undefined): Icon {
  return (nombre ? ICONOS[nombre] : undefined) ?? ReceiptIcon
}

const FRECUENCIAS: Record<RecurrenceFrequency, string> = {
  WEEKLY: 'Cada semana',
  BIWEEKLY: 'Cada 14 días',
  SEMI_MONTHLY: 'Quincenal',
  MONTHLY: 'Cada mes',
  BIMONTHLY: 'Cada 2 meses',
  QUARTERLY: 'Cada 3 meses',
  YEARLY: 'Cada año',
}

export function Recurring() {
  const navigate = useNavigate()
  const { membership } = useSession()
  const hoy = todayIn(membership.household.timezone)

  const fijos = useRecurring()
  // Instancia propia para los cobros de un toque. La hoja de monto variable usa
  // la suya, para que un error de aquí no aparezca dentro de la hoja ni al revés.
  const registrar = useRegistrarOcurrencia()
  const [cobrando, setCobrando] = useState<RecurringExpense | null>(null)

  const activos = useMemo(() => (fijos.data ?? []).filter((f) => f.active), [fijos.data])
  const inactivos = useMemo(() => (fijos.data ?? []).filter((f) => !f.active), [fijos.data])

  // Pendiente = la ocurrencia del periodo en curso todavía no se registró. Un
  // gasto anual pagado hace tres meses no es pendiente: su periodo es el año.
  const pendientes = activos.filter((f) => !f.postedThisPeriod)
  const pendienteEfectivo = sumar(pendientes.filter((f) => !f.paidWithCard))
  const pendienteTarjeta = sumar(pendientes.filter((f) => f.paidWithCard))
  const pendienteTotal = pendienteEfectivo + pendienteTarjeta

  const subtitulo = fijos.isPending
    ? 'Cargando…'
    : fijos.isError
      ? 'No pudimos cargar tus gastos fijos'
      : activos.length === 0
        ? 'Sin gastos fijos todavía'
        : `${activos.length} activos · ${formatMoney(pendienteTotal, { hideZeroCents: true })} por pagar`

  const marcarPagado = (fijo: RecurringExpense): void => {
    // Un recibo que cambia cada periodo no se puede dar por pagado con un toque:
    // el servidor exige el importe real, así que se pide en una hoja aparte.
    if (fijo.isVariableAmount) {
      haptic('light')
      setCobrando(fijo)
      return
    }
    registrar.mutate(
      { id: fijo.id },
      { onSuccess: () => haptic('success'), onError: () => haptic('error') },
    )
  }

  if (fijos.isPending) return <Cargando />

  if (fijos.isError) {
    return (
      <>
        <ScreenHeader title="Gastos fijos" subtitle={subtitulo} tinte="peach" action={<BotonNuevo />} />
        <ErrorState
          titulo="No pudimos cargar tus gastos fijos"
          mensaje={fijos.error instanceof Error ? fijos.error.message : 'Inténtalo de nuevo.'}
          onReintentar={() => void fijos.refetch()}
        />
      </>
    )
  }

  return (
    <>
      <ScreenHeader title="Gastos fijos" subtitle={subtitulo} tinte="peach" action={<BotonNuevo />} />

      <div className="px-safe mx-auto flex max-w-lg flex-col gap-4 pt-1">
        {/* El error de un cobro se queda arriba y a la vista, con la lista
            intacta: el botón sigue ahí y el segundo intento no cuesta nada. */}
        {registrar.isError ? <AvisoError mensaje={mensajeDeError(registrar.error)} /> : null}

        {activos.length === 0 && inactivos.length === 0 ? (
          <Card>
            <EmptyState
              icon={<RepeatIcon size={32} weight="fill" />}
              title="Todavía no hay gastos fijos"
              description="La renta, la luz, el gimnasio: lo que se repite se define una vez y aquí solo confirmas que cayó. Además dejamos de contarlo como dinero libre antes de que venza."
              action={
                <button
                  type="button"
                  onClick={() => {
                    haptic('light')
                    navigate('/fijos/nuevo')
                  }}
                  className="flex min-h-[44px] items-center rounded-pill bg-accion px-5 text-[14px] font-semibold text-on-accion active:scale-95"
                >
                  Agregar el primero
                </button>
              }
            />
          </Card>
        ) : null}

        {activos.length > 0 ? (
          pendienteTotal > 0 ? (
            <Card className="flex flex-col gap-4">
              <SectionHeader title="Lo que falta este periodo" className="mb-0" />
              <div className="flex items-center gap-4">
                <Donut
                  segments={segmentosPendientes(pendienteEfectivo, pendienteTarjeta)}
                  centerValue={formatMoney(pendienteTotal, { hideZeroCents: true })}
                  centerLabel="Por pagar"
                />
                <ul className="flex min-w-0 flex-1 flex-col gap-2">
                  <Leyenda
                    color="var(--chart-1)"
                    etiqueta="Sale de tus cuentas"
                    cents={pendienteEfectivo}
                  />
                  <Leyenda color="var(--chart-4)" etiqueta="Va a tarjeta" cents={pendienteTarjeta} />
                </ul>
              </div>

              {pendienteTarjeta > 0 ? (
                <p className="rounded-card bg-surface-sunken px-3 py-2.5 text-[12px] leading-snug text-ink-soft">
                  Lo que va a tarjeta no baja tu efectivo hoy: se suma al estado de cuenta y se
                  descuenta cuando venza.
                </p>
              ) : null}
            </Card>
          ) : (
            <Card className="flex items-center gap-3">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-income-soft text-income-text">
                <CheckCircleIcon size={20} weight="fill" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold text-ink">Vas al corriente</p>
                <p className="text-[12px] leading-snug text-ink-soft">
                  Todos tus gastos fijos de este periodo ya están registrados.
                </p>
              </div>
            </Card>
          )
        ) : null}

        {activos.length > 0 ? (
          <section>
            <SectionHeader title="Activos" />
            <Card className="divide-y divide-line p-0">
              {activos.map((fijo) => (
                <Fila
                  key={fijo.id}
                  fijo={fijo}
                  hoy={hoy}
                  guardando={registrar.isPending && registrar.variables?.id === fijo.id}
                  onEditar={() => navigate(`/fijos/${fijo.id}/editar`)}
                  onMarcarPagado={() => marcarPagado(fijo)}
                />
              ))}
            </Card>
          </section>
        ) : null}

        {inactivos.length > 0 ? (
          <section className="pb-2">
            <SectionHeader title="Desactivados" />
            <Card className="divide-y divide-line p-0">
              {inactivos.map((fijo) => (
                <Fila
                  key={fijo.id}
                  fijo={fijo}
                  hoy={hoy}
                  guardando={false}
                  onEditar={() => navigate(`/fijos/${fijo.id}/editar`)}
                />
              ))}
            </Card>
            <p className="mt-2 px-1 text-[12px] leading-snug text-ink-faint">
              No se borran: los movimientos que ya generaron siguen apuntando aquí. Ábrelos para
              volver a activarlos.
            </p>
          </section>
        ) : null}
      </div>

      {cobrando ? <HojaCobro fijo={cobrando} onCerrar={() => setCobrando(null)} /> : null}
    </>
  )
}

function BotonNuevo() {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={() => {
        haptic('light')
        navigate('/fijos/nuevo')
      }}
      aria-label="Nuevo gasto fijo"
      className={cn(
        'flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-pill bg-accion px-3.5',
        'text-[13px] font-semibold text-on-accion active:scale-95',
      )}
    >
      <PlusIcon size={16} weight="bold" />
      Nuevo
    </button>
  )
}

function Leyenda({ color, etiqueta, cents }: { color: string; etiqueta: string; cents: number }) {
  return (
    <li className="flex items-baseline gap-2">
      <span
        aria-hidden="true"
        className="size-2 shrink-0 translate-y-[-1px] rounded-full"
        style={{ background: color }}
      />
      <span className="line-clamp-2 text-[13px] leading-tight text-ink-soft">{etiqueta}</span>
      <span data-money className="ml-auto text-[13px] font-semibold text-ink">
        {formatMoney(cents, { hideZeroCents: true })}
      </span>
    </li>
  )
}

function Fila({
  fijo,
  hoy,
  guardando,
  onEditar,
  onMarcarPagado,
}: {
  fijo: RecurringExpense
  hoy: PlainDate
  guardando: boolean
  onEditar: () => void
  /** Ausente en los desactivados: el servidor rechaza registrarles cargos. */
  onMarcarPagado?: () => void
}) {
  // `attributedTo` nulo significa fondo común: no es de nadie en particular, así
  // que va en neutro y no con el color de quien lo dio de alta.
  const owner = fijo.attributedTo
  const palette = personPalette(owner?.personColorToken)
  const CategoriaIcon = iconoDe(fijo.category?.icon)

  const conTarjeta = fijo.paidWithCard
  const conVales = fijo.account?.type === 'MEAL_VOUCHER'
  const MedioIcon = conTarjeta ? CreditCardIcon : conVales ? BasketIcon : WalletIcon
  const medioLabel = conTarjeta
    ? `${fijo.creditCard?.name ?? 'Tarjeta'}${fijo.creditCard?.last4 ? ` ·· ${fijo.creditCard.last4}` : ''}`
    : (fijo.account?.name ?? 'Cuenta')

  const vencimiento = etiquetaVencimiento(fijo, hoy)

  return (
    <div className={cn('flex items-center gap-3 px-4 py-3', !fijo.active && 'opacity-60')}>
      {/* Tocar la fila abre su edición; el cobro es un botón hermano, no
          anidado: un botón dentro de otro no es HTML válido y en móvil ninguno
          de los dos responde de forma predecible. */}
      <button
        type="button"
        onClick={() => {
          haptic('light')
          onEditar()
        }}
        className="flex min-w-0 flex-1 items-center gap-3 text-left active:scale-[0.99]"
      >
        <span
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-full',
            owner ? palette.soft : 'bg-surface-sunken',
            owner ? palette.text : 'text-ink-soft',
          )}
        >
          <CategoriaIcon size={18} weight="fill" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 truncate text-[14px] font-medium text-ink">
            {/* El punto se colorea con `personColorToken`, nunca con el id del
                integrante: ambos son cadenas y pasar el id pintaría a todo el
                hogar del mismo color sin que el compilador diga nada. */}
            {owner ? <PersonDot token={owner.personColorToken} /> : null}
            {fijo.name}
          </span>
          <span
            className={cn(
              'block truncate text-[12px]',
              vencimiento.alerta ? 'font-medium text-expense-text' : 'text-ink-soft',
            )}
          >
            {vencimiento.texto} · {FRECUENCIAS[fijo.frequency]}
          </span>
          <span className="flex items-center gap-1 truncate text-[12px] text-ink-faint">
            <MedioIcon size={12} />
            <span className="truncate">{medioLabel}</span>
          </span>
        </span>
      </button>

      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="flex items-baseline gap-1">
          {/* El estimado se anuncia como tal: presentarlo igual que un monto
              cerrado haría cuadrar cuentas contra una cifra que aún no existe. */}
          {fijo.isVariableAmount ? (
            <span className="text-[11px] text-ink-faint">aprox.</span>
          ) : null}
          <Money cents={fijo.projectedAmountCents} />
        </span>
        {conTarjeta ? <span className="text-[10px] text-ink-faint">a crédito</span> : null}

        {!fijo.active ? (
          <span className="text-[11px] text-ink-faint">Desactivado</span>
        ) : fijo.postedThisPeriod ? (
          <span className="flex items-center gap-1 rounded-pill bg-income-soft px-2 py-0.5 text-[11px] font-medium text-income-text">
            <CheckCircleIcon size={12} weight="fill" />
            Pagado
          </span>
        ) : onMarcarPagado ? (
          <button
            type="button"
            onClick={() => {
              haptic('light')
              onMarcarPagado()
            }}
            disabled={guardando}
            className={cn(
              'flex min-h-[32px] items-center gap-1 rounded-pill border border-line bg-surface px-2.5',
              'text-[12px] font-semibold text-ink active:scale-95 disabled:opacity-60',
            )}
          >
            {guardando ? (
              <SpinnerGapIcon size={13} className="animate-spin" />
            ) : (
              <CheckCircleIcon size={13} weight="bold" />
            )}
            Ya pagué
          </button>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Hoja de cobro de un recibo variable.
 *
 * La luz y el agua no traen el mismo importe dos veces seguidas, así que el
 * servidor exige el monto real al registrar la ocurrencia. Llega precargada con
 * el estimado —que casi siempre está cerca— para que corregirlo sea teclear dos
 * cifras y no escribirlo entero.
 */
function HojaCobro({ fijo, onCerrar }: { fijo: RecurringExpense; onCerrar: () => void }) {
  const [montoCents, setMontoCents] = useState(fijo.projectedAmountCents)
  // Fecha como texto `YYYY-MM-DD`, nunca un `Date`: es un día de calendario, y
  // convertirlo a instante lo expone al error de un día al cruzar husos. Además
  // es la fecha que decide a qué estado de cuenta cae el cargo con tarjeta.
  const [fecha, setFecha] = useState<string>(fijo.nextDueOn)
  const registrar = useRegistrarOcurrencia()

  const fechaValida = isPlainDate(fecha)

  const guardar = (): void => {
    if (montoCents <= 0 || registrar.isPending) return
    // La comprobación se repite aquí dentro y no se hereda de `fechaValida`:
    // es la que convierte la cadena del input en `PlainDate` para el tipo.
    if (!isPlainDate(fecha)) return
    registrar.mutate(
      { id: fijo.id, amountCents: montoCents, occurredOn: fecha },
      {
        onSuccess: () => {
          haptic('success')
          onCerrar()
        },
        // Sin limpiar el formulario: volver a teclear el monto por un fallo de
        // red es la forma más rápida de que alguien abandone.
        onError: () => haptic('error'),
      },
    )
  }

  return (
    <FormSheet
      tinte="peach"
      titulo={fijo.name}
      onCerrar={onCerrar}
      accion={
        <div className="flex flex-col gap-2.5">
          {registrar.isError ? <AvisoError mensaje={mensajeDeError(registrar.error)} /> : null}
          <Guardar
            onClick={guardar}
            disabled={montoCents <= 0 || !fechaValida}
            guardando={registrar.isPending}
          >
            Registrar el cargo
          </Guardar>
        </div>
      }
    >
      <Campo
        label="Monto real del recibo"
        ayuda={`Lo teníamos estimado en ${formatMoney(fijo.projectedAmountCents, { hideZeroCents: true })}. Corrígelo con lo que llegó.`}
      >
        <Monto cents={montoCents} onChange={setMontoCents} />
      </Campo>

      <Campo
        label="Fecha del cargo"
        ayuda={
          fijo.paidWithCard
            ? 'Con tarjeta, esta fecha decide a qué estado de cuenta cae el cargo.'
            : 'Por defecto, el día que vencía.'
        }
        error={fechaValida ? undefined : 'Usa una fecha con formato AAAA-MM-DD'}
      >
        <input
          type="date"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
          className="min-h-[48px] w-full rounded-card border border-line bg-surface px-3.5 text-[16px] text-ink"
        />
      </Campo>
    </FormSheet>
  )
}

/**
 * Esqueleto de la lista.
 *
 * Reproduce la estructura real —la dona de 120 px, las filas de tres renglones—
 * para que el contenido aterrice donde estaba el hueco y nada se mueva bajo el
 * dedo. El subtítulo también se rellena: si apareciera después, el encabezado
 * crecería y empujaría la lista hacia abajo justo cuando el dedo ya va en camino.
 */
function Cargando() {
  return (
    <>
      <ScreenHeader title="Gastos fijos" subtitle="Cargando…" tinte="peach" action={<BotonNuevo />} />

      <div className="px-safe mx-auto flex max-w-lg flex-col gap-4 pt-1">
        <Card className="flex flex-col gap-4">
          <div className="flex h-[22px] items-center">
            <Skeleton className="h-4 w-44" />
          </div>
          <div className="flex items-center gap-4">
            <Skeleton className="size-[120px] shrink-0 rounded-full" />
            <ul className="flex min-w-0 flex-1 flex-col gap-2">
              {[0, 1].map((i) => (
                <li key={i} className="flex h-4 items-center">
                  <Skeleton className="h-3 w-full" />
                </li>
              ))}
            </ul>
          </div>
        </Card>

        <section>
          <div className="mb-3 flex h-[22px] items-center">
            <Skeleton className="h-4 w-16" />
          </div>
          <Card className="divide-y divide-line p-0">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="size-10 shrink-0 rounded-full" />
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <Skeleton className="h-[14px] w-2/5" />
                  <Skeleton className="h-[12px] w-3/5" />
                  <Skeleton className="h-[12px] w-1/3" />
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <Skeleton className="h-[14px] w-16" />
                  <Skeleton className="h-[24px] w-20 rounded-pill" />
                </div>
              </div>
            ))}
          </Card>
        </section>
      </div>
    </>
  )
}

function segmentosPendientes(efectivo: number, tarjeta: number): DonutSegment[] {
  // Colores de la rampa de gráficas, no de la paleta de identidad: un mismo tono
  // no puede significar «de Ana» en una fila y «a tarjeta» en una dona.
  return [
    { key: 'cuenta', label: 'Sale de tus cuentas', value: efectivo, color: 'var(--chart-1)' },
    { key: 'tarjeta', label: 'Va a tarjeta', value: tarjeta, color: 'var(--chart-4)' },
  ]
}

function sumar(fijos: readonly RecurringExpense[]): number {
  return fijos.reduce((total, f) => total + f.projectedAmountCents, 0)
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

function fechaCorta(fecha: PlainDate, hoy: PlainDate): string {
  const [year, month, day] = fecha.split('-')
  const [hoyYear] = hoy.split('-')
  const base = `${Number(day)} de ${MESES[Number(month) - 1]}`
  return year === hoyYear ? base : `${base} ${year}`
}

/**
 * Qué decir del vencimiento.
 *
 * `daysUntilDue` lo calcula el servidor contra el huso del hogar, no contra el
 * reloj del teléfono: a las 23:30 en México un navegador en UTC ya estaría en el
 * día siguiente y un fijo que vence hoy aparecería como vencido ayer.
 */
function etiquetaVencimiento(
  fijo: RecurringExpense,
  hoy: PlainDate,
): { texto: string; alerta: boolean } {
  if (!fijo.active) return { texto: 'Sin vencimientos', alerta: false }
  if (fijo.postedThisPeriod) {
    return { texto: `Siguiente ${fechaCorta(fijo.nextDueOn, hoy)}`, alerta: false }
  }

  const dias = fijo.daysUntilDue
  if (dias < 0) {
    const atraso = -dias
    return { texto: `Venció hace ${atraso} ${atraso === 1 ? 'día' : 'días'}`, alerta: true }
  }
  if (dias === 0) return { texto: 'Vence hoy', alerta: true }
  if (dias === 1) return { texto: 'Vence mañana', alerta: false }
  return { texto: `Vence el ${fechaCorta(fijo.nextDueOn, hoy)}`, alerta: false }
}

/**
 * Un «Revisa los datos» sin más no dice qué corregir; el detalle por campo que
 * manda el servidor sí.
 */
export function mensajeDeError(error: unknown): string {
  if (error instanceof ApiError) {
    const detalles = Object.values(error.fieldErrors ?? {}).flat()
    return detalles.length > 0 ? detalles.join('. ') : error.message
  }
  return 'No pudimos guardar. Inténtalo de nuevo.'
}
